import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { map, catchError, switchMap, take, filter, first } from 'rxjs/operators';
import { CaseService } from './case.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class IpWhitelistService {
  private whitelistStatus$ = new BehaviorSubject<boolean | null>(null);
  private currentIp$ = new BehaviorSubject<string>('');
  private isChecking = false;
  private checkPromise: Promise<boolean> | null = null;

  constructor(private http: HttpClient, private caseService: CaseService) {}

  private isDevelopmentEnvironment(): boolean {
    const hostname = window.location.hostname;
    return !environment.production && (hostname === 'localhost');
  }

  getClientIp(): Observable<string> {

    if (this.isDevelopmentEnvironment()) {
      return of('127.0.0.1');
    }

    return this.http.get<{ip: string}>('https://ipapi.co/json/')
      .pipe(
        map(response => {
          return response.ip;
        }),
        catchError(error => {
          console.error('Error getting IP from primary API:', error);
          return this.http.get<{origin: string}>('https://httpbin.org/ip')
            .pipe(
              map(response => response.origin),
              catchError(fallbackError => {
                console.error('Error getting IP from fallback API:', fallbackError);
                return of('127.0.0.1');
              })
            );
        }),
        take(1)
      );
  }

  getCurrentIp(): Observable<string> {
    return this.currentIp$.asObservable();
  }

  getWhitelistStatus(): Observable<boolean> {
    if (this.whitelistStatus$.value !== null && !this.isChecking) {
      return of(this.whitelistStatus$.value);
    }

    if (this.checkPromise) {
      return new Observable<boolean>(observer => {
        this.checkPromise!.then(status => {
          observer.next(status);
          observer.complete();
        }).catch(error => {
          observer.error(error);
        });
      });
    }

    this.checkPromise = new Promise<boolean>((resolve, reject) => {
      const portalSettings = JSON.parse(localStorage.getItem('portal_settings') || '{}');
      const ipRanges = portalSettings?.portal_details?.ip_address_ranges;

      if (this.isDevelopmentEnvironment()) {
        console.log('Development environment detected - bypassing IP whitelist check');
        this.whitelistStatus$.next(true);
        resolve(true);
        return;
      }

      if (!ipRanges || (Array.isArray(ipRanges) && ipRanges.length === 0)) {
        console.log('No IP address ranges configured - allowing access without IP check');
        this.whitelistStatus$.next(true);
        resolve(true);
        return;
      }

      this.isChecking = true;
      this.getClientIp().pipe(
        switchMap(ip => {
          this.currentIp$.next(ip);
          localStorage.setItem('last_checked_ip', ip);

          const data = {
            user_ip_address: ip,
            ip_address_ranges: ipRanges
          };

          return this.caseService.getWhitelistUser(data);
        }),
        take(1)
      ).subscribe({
        next: (response) => {
          let isWhitelisted = false;

          if (Array.isArray(response)) {
            isWhitelisted = response[0] === true;
          }
          else if (typeof response === 'object' && response !== null) {
            isWhitelisted = (response as { isWhitelisted?: boolean }).isWhitelisted === true;
          }

          this.whitelistStatus$.next(isWhitelisted);
          this.isChecking = false;
          this.checkPromise = null;
          resolve(isWhitelisted);
        },
        error: (error) => {
          this.whitelistStatus$.next(false);
          this.isChecking = false;
          this.checkPromise = null;
          reject(error);
        }
      });
    });

    return new Observable<boolean>(observer => {
      this.checkPromise!.then(status => {
        observer.next(status);
        observer.complete();
      }).catch(error => {
        observer.error(error);
      });
    });
  }

  refreshIpCheck(): Observable<boolean> {
    this.whitelistStatus$.next(null);
    this.isChecking = false;
    this.checkPromise = null;

    return this.getWhitelistStatus();
  }
}
